/** Shared helpers for fraction and decimal answers (Levels 5+). */
import type { InputSpec } from "../types";

export const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : Math.abs(a));
export const lcm = (a: number, b: number) => (a * b) / gcd(a, b);
export const simp = (n: number, d: number): [number, number] => { const g = gcd(n, d) || 1; return [n / g, d / g]; };

/** A fraction answer "n/d" plus its unsimplified forms (the book simplifies "if you can"; simplifying is never required unless the item says so). */
export function fracAnswers(n: number, d: number, requireSimplest = false): { answer: string; alsoAccept: string[] } {
  const [a, b] = simp(n, d);
  const also = new Set<string>();
  if (!requireSimplest) for (let k = 1; k <= 12; k++) if (a * k !== n || b * k !== d || k > 1) also.add(`${a * k}/${b * k}`);
  also.add(`${n}/${d}`);
  also.delete(`${a}/${b}`);
  return { answer: `${a}/${b}`, alsoAccept: [...also] };
}

/** Input for a mixed number: [whole] [top]/[bottom]. */
export const MIXED: InputSpec = { kind: "fields", fields: [{ width: 2, label: "" }, { width: 2 }, { label: "/", width: 2 }] };
/** A mixed-number answer "w|n|d" (fraction part simplified), plus unsimplified fraction parts. */
export function mixedAnswers(num: number, den: number, requireSimplest = false): { answer: string; alsoAccept: string[] } {
  const w = Math.floor(num / den), r = num % den;
  const [a, b] = simp(r, den);
  const also = new Set<string>();
  if (!requireSimplest) for (let k = 2; k * b <= 60; k++) also.add(`${w}|${a * k}|${b * k}`);
  also.add(`${w}|${r}|${den}`);
  also.delete(`${w}|${a}|${b}`);
  return { answer: `${w}|${a}|${b}`, alsoAccept: [...also] };
}
/** Decimal answers: the canonical form plus trailing-zero and leading-point variants (4.1 = 4.10; 0.5 = .5). */
export function decAnswers(x: number, places = 4): { answer: string; alsoAccept: string[] } {
  const s = String(Number(x.toFixed(places)));
  const also = new Set<string>();
  const [i, f = ""] = s.split(".");
  for (let z = f.length; z <= 3; z++) if (z > 0) also.add(`${i}.${f.padEnd(z, "0")}`);
  if (i === "0" && f) also.add(`.${f}`);
  also.delete(s);
  return { answer: s, alsoAccept: [...also] };
}
/** A decimal with p places from an integer count of its smallest pieces, as a string ("362", 2 → "3.62"). */
export const decStr = (pieces: number, p: number) => (pieces / 10 ** p).toFixed(p);
